import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Building2, ChevronDown, X } from 'lucide-react';
import { Button } from './ui/button';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface Association {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
}

interface AssociationSelectorProps {
  selectedAssociation: Association | null;
  onSelect: (association: Association | null) => void;
  disabled?: boolean;
  className?: string;
}

const AssociationSelector: React.FC<AssociationSelectorProps> = ({
  selectedAssociation,
  onSelect,
  disabled = false,
  className = ''
}) => {
  const [associations, setAssociations] = useState<Association[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchAssociations();
  }, []);

  const fetchAssociations = async () => {
    try {
      const res = await axios.get(`${API}/associations/public/list`);
      setAssociations(res.data);
    } catch (error) {
      console.error('Error fetching associations:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAssociations = associations.filter(a => 
    a.name.toLowerCase().includes(search.toLowerCase()) ||
    a.slug.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (association: Association) => {
    onSelect(association);
    setShowDialog(false);
    setSearch('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(null);
  };

  if (loading || associations.length === 0) {
    return null; // Non mostrare se non ci sono associazioni
  }

  return (
    <>
      <div className={`space-y-2 ${className}`}>
        <Label className="text-sm text-muted-foreground">
          Fai parte di un'associazione? (opzionale)
        </Label>
        <Button
          type="button"
          variant="outline"
          className="w-full justify-between h-auto py-3"
          onClick={() => setShowDialog(true)}
          disabled={disabled}
          data-testid="association-selector-trigger"
        >
          {selectedAssociation ? (
            <div className="flex items-center gap-3">
              {selectedAssociation.logo_url ? (
                <img 
                  src={selectedAssociation.logo_url} 
                  alt="" 
                  className="h-6 w-6 rounded object-contain"
                />
              ) : (
                <Building2 className="h-5 w-5 text-primary" />
              )}
              <div className="text-left">
                <div className="font-medium">{selectedAssociation.name}</div>
                <div className="text-xs text-muted-foreground">
                  {selectedAssociation.slug}.ops.online
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Building2 className="h-5 w-5" />
              <span>Seleziona un'associazione</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            {selectedAssociation && (
              <X 
                className="h-4 w-4 text-muted-foreground hover:text-foreground" 
                onClick={handleClear}
              />
            )}
            <ChevronDown className="h-4 w-4" />
          </div>
        </Button>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Seleziona Associazione</DialogTitle>
            <DialogDescription>
              Scegli l'associazione di cui fai parte per accedere alle funzionalità Groupage
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Input
              placeholder="Cerca associazione..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="association-search"
            />

            <div className="max-h-60 overflow-y-auto space-y-2">
              {filteredAssociations.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">
                  Nessuna associazione trovata
                </p>
              ) : (
                filteredAssociations.map((association) => (
                  <button
                    key={association.id}
                    type="button"
                    onClick={() => handleSelect(association)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors hover:bg-muted
                      ${selectedAssociation?.id === association.id 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border'
                      }`}
                    data-testid={`association-option-${association.slug}`}
                  >
                    {association.logo_url ? (
                      <img 
                        src={association.logo_url} 
                        alt="" 
                        className="h-8 w-8 rounded object-contain"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded bg-primary/10 flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-primary" />
                      </div>
                    )}
                    <div className="text-left flex-1">
                      <div className="font-medium">{association.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {association.slug}.ops.online
                      </div>
                    </div>
                    {selectedAssociation?.id === association.id && (
                      <div className="h-5 w-5 rounded-full bg-primary text-white flex items-center justify-center">
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>

            <Button
              variant="ghost"
              className="w-full"
              onClick={() => {
                onSelect(null);
                setShowDialog(false);
              }}
            >
              Continua senza associazione
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AssociationSelector;
